import { sql } from "drizzle-orm";
import { index, integer, jsonb, pgTable, timestamp, uniqueIndex, varchar, text, boolean } from "drizzle-orm/pg-core";
import type { AdminPermission, AdminRole } from "../adminPermissions";

// Session storage table.
// (IMPORTANT) This table is mandatory for Replit Auth, don't drop it.
export const sessions = pgTable(
  "sessions",
  {
    sid: varchar("sid").primaryKey(),
    sess: jsonb("sess").notNull(),
    expire: timestamp("expire").notNull(),
  },
  (table) => [index("IDX_session_expire").on(table.expire)]
);

// Iraqi Provinces (المحافظات العراقية) - مرتبة أبجدياً
export const IRAQI_PROVINCES = [
  "بغداد",
  "الأنبار",
  "أربيل",
  "البصرة",
  "بابل",
  "دهوك",
  "ديالى",
  "ذي قار",
  "السليمانية",
  "صلاح الدين",
  "القادسية",
  "كربلاء",
  "كركوك",
  "المثنى",
  "نينوى",
  "ميسان",
  "النجف",
  "واسط",
  "أخرى",
] as const;

// Account types
export const ACCOUNT_TYPES = [
  "buyer",           // مشتري/زبون عادي
  "wholesaler",      // تاجر جملة
  "reseller",        // بائع تجزئة افتراضي (يشتري من الجملة ويبيع بالتجزئة)
] as const;

// Shop types for retail sellers (أنواع المحلات لأصحاب المحلات)
export const SHOP_TYPES = [
  "wholesale",  // محل جملة
  "retail",     // محل مفرد
] as const;

// Wholesaler location types (أنواع مواقع تجار الجملة)
export const WHOLESALER_LOCATION_TYPES = [
  "wholesale_shop",    // صاحب محل جملة داخل العراق
  "importer_iraq",     // مستورد من الصين/تركيا يعمل داخل العراق
  "exporter_abroad",   // مُصدِّر من الصين/تركيا خارج العراق
] as const;

// Baghdad wholesale markets (أسواق الجملة في بغداد) - مرتبة أبجدياً
export const BAGHDAD_MARKETS = [
  "السنك",
  "السوق العربي",
  "الشورجة",
  "المتنبي",
  "جميلة",
  "شارع الصناعة",
  "شورجة البياع",
  "عگد النصارى",
  "مجمع المشن",
  "مريدي",
  "أخرى",
] as const;

export const SECURITY_QUESTIONS = [
  "ما هو اسم أمك؟",
  "ما هو اسم مدرستك الابتدائية؟",
  "ما هي مدينة ولادتك؟",
  "ما هو اسم صديق طفولتك المفضل؟",
  "ما هو اسم حيوانك الأليف الأول؟",
  "ما هو لون سيارتك الأولى؟",
] as const;

// User storage table.
// (IMPORTANT) This table is mandatory for Replit Auth, don't drop it.
export const users = pgTable("users", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: varchar("email").unique(),
  firstName: varchar("first_name"),
  lastName: varchar("last_name"),
  profileImageUrl: varchar("profile_image_url"),
  phone: varchar("phone").unique(),
  province: varchar("province"),
  passwordHash: text("password_hash"),
  // Bumped to invalidate every previously-issued native Bearer token for this
  // user (on password change, or admin/owner "log out everywhere"). New tokens
  // embed the current value; bearerAuth rejects tokens whose value is stale.
  tokenVersion: integer("token_version").notNull().default(1),
  isPremiumAccount: boolean("is_premium_account").default(false),
  officeName: varchar("office_name"),
  officeAddress: text("office_address"),
  isAdmin: boolean("is_admin").default(false),
  adminRole: varchar("admin_role").$type<AdminRole | null>(),
  adminPermissions: jsonb("admin_permissions").$type<AdminPermission[]>().default(sql`'[]'::jsonb`),
  adminDisabled: boolean("admin_disabled").default(false),
  isSuspended: boolean("is_suspended").default(false),
  accountType: varchar("account_type").default("buyer").$type<"buyer" | "wholesaler" | "reseller">(),
  // Retail seller fields (صاحب محل جملة او مفرد)
  shopName: varchar("shop_name"),
  shopAddress: text("shop_address"),
  shopCity: varchar("shop_city"),
  shopDistrict: varchar("shop_district"),
  shopNeighborhood: varchar("shop_neighborhood"),
  shopType: varchar("shop_type"), // wholesale (جملة) or retail (مفرد)
  // Despite the "retail" in its name, this flag is only ever read/written
  // together with `account_type = 'wholesaler'` (see the admin approve/reject
  // and pending-wholesaler queries). It means: this WHOLESALER's registration
  // has been approved by an admin. The name is legacy and cannot be changed
  // without a data migration.
  isRetailApproved: boolean("is_retail_approved").default(false),
  // The timestamp half of the flag above. Named for what it actually is —
  // wholesaler approval — rather than mirroring the legacy `retail` wording,
  // so nobody later reads it as "approved as a retail shop owner". The daily
  // report must separate "registered during the period" from "approved during
  // the period", and a boolean carries no time. NULL on rows approved before
  // this column existed (surfaced in the report's `limitations`); set going
  // forward, in the same UPDATE as the flag, by the approve endpoint.
  wholesalerApprovedAt: timestamp("wholesaler_approved_at"),
  // Wholesaler fields (تاجر جملة)
  wholesalerLocationType: varchar("wholesaler_location_type"), // wholesale_shop, importer_iraq, exporter_abroad
  businessName: varchar("business_name"),
  businessAddress: text("business_address"),
  businessCity: varchar("business_city"),
  businessCountry: varchar("business_country"), // للمصدرين من خارج العراق
  baghdadMarket: varchar("baghdad_market"), // سوق الجملة في بغداد (الشورجة، جميلة، إلخ)
  importExportCountries: text("import_export_countries"), // الصين، تركيا، إلخ
  // Store seller fields (دور "المحل" التجاري)
  // وجود محل حقيقي ليس دوراً مستقلاً؛ بل صفة إضافية فوق نفس دور المتجر.
  virtualStoreName: varchar("virtual_store_name"), // اسم المتجر الافتراضي
  virtualStoreDescription: text("virtual_store_description"), // وصف المتجر الافتراضي
  virtualStorePhone: varchar("virtual_store_phone"), // رقم هاتف المتجر
  virtualStoreSpecialty: varchar("virtual_store_specialty"), // اختصاص المتجر (computers, phones, etc.)
  customSpecialty: varchar("custom_specialty"), // اختصاص مخصص عند اختيار "أخرى"
  virtualStoreAvatarUrl: varchar("virtual_store_avatar_url"), // Custom uploaded store image
  virtualStoreAvatarPreset: varchar("virtual_store_avatar_preset"), // Preset icon selection (e.g., 'shop', 'bag', 'star', etc.)
  isVirtualStore: boolean("is_virtual_store").default(false),
  // When the virtual store was activated. Stores live as flags on the user
  // row, so without this the only available time is the USER's signup time —
  // which is wrong for "stores created during the period" whenever a
  // long-standing customer opens a store later. Set once, on activation.
  virtualStoreCreatedAt: timestamp("virtual_store_created_at"),
  hasPhysicalStore: boolean("has_physical_store").default(false), // real-world storefront flag over the same store role
  // Same reasoning as virtualStoreCreatedAt, for the physical storefront.
  physicalStoreCreatedAt: timestamp("physical_store_created_at"),
  // High-water mark for the shared admin-broadcast inbox: broadcasts created
  // at/before this timestamp are considered "read" by this user. Personal
  // notifications track read state per-row in `user_notifications`; broadcasts
  // are shared so we can't, hence this single marker. Added at runtime via
  // ALTER TABLE ... ADD COLUMN IF NOT EXISTS (server/lib/userNotificationsStore.ts).
  broadcastsReadAt: timestamp("broadcasts_read_at"),
  physicalStoreName: varchar("physical_store_name"),
  physicalStoreProvince: varchar("physical_store_province"),
  physicalStoreArea: varchar("physical_store_area"),
  physicalStoreNeighborhood: varchar("physical_store_neighborhood"), // المحلة
  physicalStoreLandmark: varchar("physical_store_landmark"), // أقرب نقطة دالة
  physicalStoreAddress: text("physical_store_address"),
  physicalStorePhone: varchar("physical_store_phone"),
  physicalStoreSpecialty: varchar("physical_store_specialty"),
  physicalStoreDescription: text("physical_store_description"),
  inventoryLimit: integer("inventory_limit").default(20),
  // Saved cart-checkout shipping addresses, synced across the user's devices
  // (web + iOS + Android). Mirrors the client SavedCheckoutAddressBook shape:
  // { addresses: [...], defaultAddressId: string | null }.
  checkoutAddressBook: jsonb("checkout_address_book"),
  // Per-user free personal-listing allowance for non-store buyers.
  // Default 2; admin-adjustable in [2, 50000]. Backend clamps defensively.
  freeListingAllowance: integer("free_listing_allowance").default(2),
  isFeatured: boolean("is_featured").default(false),
  featuredUntil: timestamp("featured_until"),
  securityQuestion: varchar("security_question"),
  securityAnswerHash: text("security_answer_hash"),
  lastLoginAt: timestamp("last_login_at"),
  // Last time a visitor opened this virtual store's page
  // (GET /api/store/:userId/products). Together with createdAt and
  // lastLoginAt it forms the store's "effective activity": a store is only
  // hidden from public feeds after STORE_ACTIVITY_HIDE_DAYS with no visit
  // AND no owner login. A single customer visit revives a hidden store.
  virtualStoreLastVisitedAt: timestamp("virtual_store_last_visited_at"),
  isFrozen: boolean("is_frozen").default(false),
  autoCancelStrikes: integer("auto_cancel_strikes").default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow().$onUpdate(() => new Date()),
}, (table) => [
  // Case-insensitive uniqueness for the virtual-store display name.
  // We normalize by lower(trim(...)) so "Iraq Sooq" and "iraq sooq" or
  // "  متجري  " and "متجري" all collapse to the same slot. The partial
  // WHERE clause skips users who haven't picked a store name yet, so
  // the constraint never blocks non-store registrations.
  uniqueIndex("users_virtual_store_name_lower_unique")
    .on(sql`lower(trim(${table.virtualStoreName}))`)
    .where(sql`${table.virtualStoreName} IS NOT NULL AND length(trim(${table.virtualStoreName})) > 0`),
]);

export type UpsertUser = typeof users.$inferInsert;
export type User = typeof users.$inferSelect;

// Virtual store edit requests table (طلبات تعديل المتجر الافتراضي)
export const virtualStoreEditRequests = pgTable("virtual_store_edit_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id),
  requestedStoreName: varchar("requested_store_name"),
  requestedStorePhone: varchar("requested_store_phone"),
  requestedSpecialty: varchar("requested_specialty"),
  requestedCustomSpecialty: varchar("requested_custom_specialty"),
  requestedDescription: text("requested_description"),
  currentStoreName: varchar("current_store_name"),
  currentStorePhone: varchar("current_store_phone"),
  currentSpecialty: varchar("current_specialty"),
  currentCustomSpecialty: varchar("current_custom_specialty"),
  currentDescription: text("current_description"),
  requestedProvince: varchar("requested_province"),
  requestedArea: varchar("requested_area"),
  requestedAddress: text("requested_address"),
  currentProvince: varchar("current_province"),
  currentArea: varchar("current_area"),
  currentAddress: text("current_address"),
  reason: text("reason"),
  status: varchar("status").default("pending").$type<"pending" | "approved" | "rejected">(),
  adminNote: text("admin_note"),
  createdAt: timestamp("created_at").defaultNow(),
  processedAt: timestamp("processed_at"),
});

export type VirtualStoreEditRequest = typeof virtualStoreEditRequests.$inferSelect;
export type InsertVirtualStoreEditRequest = typeof virtualStoreEditRequests.$inferInsert;

// Physical store edit requests table (طلبات تعديل المحل الحقيقي)
export const physicalStoreEditRequests = pgTable("physical_store_edit_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id),
  requestedStoreName: varchar("requested_store_name"),
  requestedStorePhone: varchar("requested_store_phone"),
  requestedStoreSpecialty: varchar("requested_store_specialty"),
  requestedStoreDescription: text("requested_store_description"),
  requestedProvince: varchar("requested_province"),
  requestedArea: varchar("requested_area"),
  requestedAddress: text("requested_address"),
  currentStoreName: varchar("current_store_name"),
  currentStorePhone: varchar("current_store_phone"),
  currentStoreSpecialty: varchar("current_store_specialty"),
  currentStoreDescription: text("current_store_description"),
  currentProvince: varchar("current_province"),
  currentArea: varchar("current_area"),
  currentAddress: text("current_address"),
  reason: text("reason"),
  status: varchar("status").default("pending").$type<"pending" | "approved" | "rejected">(),
  adminNote: text("admin_note"),
  createdAt: timestamp("created_at").defaultNow(),
  processedAt: timestamp("processed_at"),
});

export type PhysicalStoreEditRequest = typeof physicalStoreEditRequests.$inferSelect;
export type InsertPhysicalStoreEditRequest = typeof physicalStoreEditRequests.$inferInsert;
